import { AgentRuntime, logger, type UUID, type Project as CoreProject, type ProjectAgent } from "@elizaos/core";
import { resolve } from "node:path";
import { readFileSync } from "node:fs";
import { v4 as uuidv4 } from "uuid";

export interface Project {
  runtime: AgentRuntime;
  path: string;
  agent: ProjectAgent;
}

/**
 * Loads a project from the specified path
 * @param path Path to the project directory
 * @returns The loaded project
 */
export async function loadProject(path: string): Promise<Project> {
  try {
    const projectPath = resolve(path);
    logger.info(`Loading project from ${projectPath}`);
    
    // Load project configuration
    let projectConfig: CoreProject;
    try {
      const configPath = resolve(projectPath, "eliza.config.json");
      logger.info(`Loading config from ${configPath}`);
      
      try {
        const configContent = readFileSync(configPath, 'utf8');
        projectConfig = JSON.parse(configContent);
        logger.info("Successfully loaded config:", projectConfig);
      } catch (error) {
        logger.error("Failed to load config file:", error);
        throw new Error(`Failed to load config: ${error.message}`);
      }
      
      if (!projectConfig) {
        logger.error("Config file is empty");
        throw new Error("Config file is empty");
      }
      
      if (!projectConfig.agents || !Array.isArray(projectConfig.agents)) {
        logger.error("Invalid config format:", projectConfig);
        throw new Error("Config file must have an agents array");
      }
    } catch (error) {
      logger.warn("No eliza.config.json found, using default configuration");
      projectConfig = {
        agents: [{
          character: {
            name: "Test Agent",
            bio: ["A test agent for running automated tests"],
            templates: {},
            settings: {},
            system: "You are a test agent for running automated tests.",
            messageExamples: []
          }
        }]
      };
    }

    // Use the first agent as our test agent
    const agent = projectConfig.agents[0];
    if (!agent) {
      logger.error("No agents defined in project configuration");
      throw new Error("No agents defined in project configuration");
    }

    logger.info("Creating runtime with agent:", agent);

    // Initialize runtime with agent configuration
    const runtime = new AgentRuntime({
      agentId: uuidv4() as UUID,
      character: agent.character,
      plugins: agent.plugins || []
    });
    
    // Initialize the agent if it has an init function
    if (agent.init) {
      await agent.init(runtime);
    }
    
    return {
      runtime,
      path: projectPath,
      agent
    };
  } catch (error) {
    logger.error("Failed to load project:", error);
    throw error;
  }
} 